import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE } from '@ag-grid-community/locale';
import { ListItem } from '../model/list';

interface GridRow {
  identifier: string;
  title: string;
  subtitle: string;
  raw: any;
}

const DEFAULT_COLUMN_DEFS: ColDef<GridRow>[] = [
  { field: 'title', headerName: 'Titel', flex: 2 },
  { field: 'subtitle', headerName: 'Info', flex: 2 },
];

/**
 * ag-grid based drop-in replacement for FilterListComponent — same
 * @Input/@Output contract, used by ComponentHandlerComponent for object
 * types whose list should render as a grid (currently User, Person,  Event).
 */
@Component({
  selector: 'app-generic-grid-list',
  templateUrl: './generic-grid-list.component.html',
  styleUrls: ['./generic-grid-list.component.css'],
})
export class GenericGridListComponent implements OnInit {

  @Input() set data(d: ListItem[]) {
    this.items = d ?? [];
    this.itemsByIdentifier.clear();
    this.rowData = this.items.map((item, i) => {
      const identifier = (item.data as any)?.identifier || `row-${i}`;
      this.itemsByIdentifier.set(identifier, item);
      return {
        identifier,
        title: item.title ?? '',
        subtitle: (item as any).subTitle ?? (item as any).subtitle ?? '',
        raw: item.data,
      };
    });
    this.refreshGrid();
  }
  @Input() showAdd = true;
  @Input() showFilter = true;
  @Input() showMultiEdit = false;
  @Input() multiEditLabelKey = 'btn_edit';
  @Input() set loadingPagination(l: boolean) {
    this.loading = l;
  }
  @Input() set columnDefs(cols: ColDef<GridRow>[] | null | undefined) {
    this._columnDefs = cols?.length ? cols : DEFAULT_COLUMN_DEFS;
  }
  get columnDefs(): ColDef<GridRow>[] {
    return this._columnDefs;
  }

  @Output() addItem: EventEmitter<void> = new EventEmitter();
  @Output() addItemFromList: EventEmitter<void> = new EventEmitter();
  /** Emits the selected ListItems when the caller opts in via showMultiEdit. */
  @Output() multiEdit: EventEmitter<ListItem[]> = new EventEmitter();

  items: ListItem[] = [];
  private itemsByIdentifier = new Map<string, ListItem>();
  rowData: GridRow[] = [];
  selectedRows: GridRow[] = [];
  loading = false;
  filterText = '';

  private gridApi?: GridApi<GridRow>;
  private _columnDefs: ColDef<GridRow>[] = DEFAULT_COLUMN_DEFS;

  localeText = AG_GRID_LOCALE_DE;
  defaultColDef: ColDef = { sortable: true, filter: true, resizable: true };

  gridOptions: GridOptions = {
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: false,
      checkboxes: true,
      headerCheckbox: true,
      selectAll: 'filtered',
    } as any,
    animateRows: true,
    getRowId: (params: any) => params.data.identifier,
    getMainMenuItems: (params) =>
      params.defaultItems.filter((i: string) => i !== 'columnChooser' && i !== 'resetColumns'),
    getContextMenuItems: (params) =>
      (params.defaultItems ?? []).filter(
        (i: string) => !['cut', 'paste', 'export', 'separator', 'copyWithGroupHeaders'].includes(i),
      ),
  };

  ngOnInit(): void {}

  private refreshGrid(): void {
    this.gridApi?.updateGridOptions({ rowData: this.rowData });
  }

  onGridReady(event: GridReadyEvent<GridRow>): void {
    this.gridApi = event.api;
  }

  onSelectionChanged(): void {
    this.selectedRows = this.gridApi?.getSelectedRows() ?? [];
  }

  onFilterTextChanged(value: string): void {
    this.filterText = value;
    this.gridApi?.setGridOption('quickFilterText', value);
  }

  onRowClicked(row: GridRow): void {
    this.itemsByIdentifier.get(row.identifier)?.onClick();
  }

  deleteSelected(): void {
    this.selectedRows.forEach(row => this.itemsByIdentifier.get(row.identifier)?.onDelete());
  }

  openMultiEdit(): void {
    if (!this.selectedRows.length) return;
    const items = this.selectedRows
      .map(row => this.itemsByIdentifier.get(row.identifier))
      .filter((item): item is ListItem => !!item);
    this.multiEdit.emit(items);
  }
}
